// Pinned + Recently-viewed accounts (role-based redesign personalization layer).
// A per-browser store of orgs the user pinned (★) and the orgs they recently
// opened, surfaced at the top of the rail. Pure presentation — no server state.
// Components subscribe via `subscribePins` so the rail updates the instant a pin
// is toggled or a recent is recorded (localStorage events don't fire in the same
// tab, so we notify in-process).

export interface PinnedOrg {
  id: string;
  name: string;
}

const PIN_KEY = 'funnel:pins';
const RECENT_KEY = 'funnel:recents';
const RECENT_CAP = 6;

const listeners = new Set<() => void>();
function emit(): void {
  listeners.forEach((l) => l());
}
export function subscribePins(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function read(key: string): PinnedOrg[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v.filter((o) => o && typeof o.id === 'string') : [];
  } catch {
    return [];
  }
}
function write(key: string, list: PinnedOrg[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* private mode / quota */
  }
  emit();
}

export function getPins(): PinnedOrg[] {
  return read(PIN_KEY);
}
export function isPinned(id: string): boolean {
  return read(PIN_KEY).some((o) => o.id === id);
}
/** Toggle a pin; returns the new list. */
export function togglePin(org: PinnedOrg): PinnedOrg[] {
  const cur = read(PIN_KEY);
  const next = cur.some((o) => o.id === org.id)
    ? cur.filter((o) => o.id !== org.id)
    : [{ id: org.id, name: org.name }, ...cur].slice(0, 12);
  write(PIN_KEY, next);
  return next;
}

export function getRecents(): PinnedOrg[] {
  return read(RECENT_KEY);
}
/** Record an opened org as a recent (dedup, newest first, capped). */
export function recordRecent(org: PinnedOrg): void {
  if (!org.id) return;
  const cur = read(RECENT_KEY).filter((o) => o.id !== org.id);
  write(RECENT_KEY, [{ id: org.id, name: org.name }, ...cur].slice(0, RECENT_CAP));
}
